import type { FC, ReactNode } from 'react';
import { Dropdown, type DropdownProps, type MenuProps } from 'antd';
import { TbCircleCheckFilled, TbHistory, TbProgressCheck, TbTrash } from 'react-icons/tb';

import { DropdownMenuPanel } from '@/shared/components/dropdowns/DropdownPanel';
import type { Task } from '@/types';

import { getTaskCompletionActionState } from './helpers';

interface TaskActionsDropdownProps {
    canEdit: boolean;
    children: ReactNode;
    deleteLabel?: string;
    disabled?: boolean;
    onDelete: () => Promise<void> | void;
    onOpenChange?: (open: boolean) => void;
    onSetCompleted: (completed: boolean) => Promise<void> | void;
    onShowHistory?: () => void;
    placement?: DropdownProps['placement'];
    tasks: ReadonlyArray<Pick<Task, 'completed'>>;
}

const TaskActionsDropdown: FC<TaskActionsDropdownProps> = ({
    canEdit,
    children,
    deleteLabel = 'Delete Task',
    disabled,
    onDelete,
    onOpenChange,
    onSetCompleted,
    onShowHistory,
    placement = 'bottomRight',
    tasks,
}): JSX.Element => {
    const completionAction = getTaskCompletionActionState(tasks);
    const items: MenuProps['items'] = [
        ...(canEdit
            ? [
                  {
                      key: 'toggle-completed',
                      icon: completionAction.nextCompleted ? (
                          <TbCircleCheckFilled size='1rem' />
                      ) : (
                          <TbProgressCheck size='1rem' />
                      ),
                      label: completionAction.label,
                      onClick: () => void onSetCompleted(completionAction.nextCompleted),
                  },
                  { type: 'divider' as const },
              ]
            : []),
        {
            key: 'history',
            disabled: !onShowHistory,
            icon: <TbHistory size='1rem' />,
            label: 'Show History',
            onClick: onShowHistory,
        },
        ...(canEdit
            ? [
                  {
                      key: 'delete',
                      danger: true,
                      icon: <TbTrash size='1rem' />,
                      label: deleteLabel,
                      onClick: () => void onDelete(),
                  },
              ]
            : []),
    ];

    return (
        <Dropdown
            disabled={disabled}
            popupRender={() => <DropdownMenuPanel {...{ items }} />}
            onOpenChange={onOpenChange}
            placement={placement}
            trigger={['click']}
        >
            {children}
        </Dropdown>
    );
};

export default TaskActionsDropdown;
